import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const CHANNEL_START = 0x0010
const CHANNEL_SIZE = 16
type BaofengDigitalSpec = {
  id: string
  model: string
  summary: string
  serialId: number
  serialMagic: readonly [number, number]
  channelCount: number
  v12Unlock: boolean
}

export type BaofengDigitalProfile = {
  protocol: 'baofeng-digital'
  id: string
  vendor: 'Baofeng'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  memorySize: number
  serialId: number
  serialMagic: readonly [number, number]
  v12Unlock: boolean
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly BaofengDigitalSpec[] = [
  {
    id: 'baofeng-w31d',
    model: 'W31D',
    summary: 'Baofeng W31D compact digital/analog handheld.',
    serialId: 0x04,
    serialMagic: [0x80, 0x82],
    channelCount: 30,
    v12Unlock: false,
  },
  {
    id: 'baofeng-bf-t20d',
    model: 'BF-T20D',
    summary: 'Baofeng BF-T20D compact digital/analog handheld.',
    serialId: 0x05,
    serialMagic: [0x80, 0x82],
    channelCount: 30,
    v12Unlock: false,
  },
  {
    id: 'baofeng-bf-v12d',
    model: 'BF-V12D',
    summary: 'Baofeng BF-V12D compact digital/analog handheld.',
    serialId: 0x0a,
    serialMagic: [0x20, 0x22],
    channelCount: 16,
    v12Unlock: true,
  },
]

export const BAOFENG_DIGITAL_PROFILES: readonly BaofengDigitalProfile[] =
  SPECS.map((spec) => ({
    protocol: 'baofeng-digital',
    id: spec.id,
    vendor: 'Baofeng',
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'Read + write', `${spec.channelCount} channels`, 'No channel names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channelCount,
    nameLength: 0,
    memorySize: 0x3e0,
    serialId: spec.serialId,
    serialMagic: spec.serialMagic,
    v12Unlock: spec.v12Unlock,
    powerLevels: ['Low', 'High'],
  }))

function memoryChunks(profile: BaofengDigitalProfile): readonly (readonly [number, number, boolean])[] {
  return [
    [0x000, profile.channelMaximum > 16 ? 0x1ef : 0x10f, false],
    [0x2b0, 0x2bf, false],
    [0x330, 0x33f, true],
    [0x380, 0x3df, false],
  ]
}

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeTone(raw: Uint8Array): {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
} {
  if (raw.every((value) => value === 0xff || value === 0)) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  const digital = (raw[1] & 0x80) !== 0
  const polarity = (raw[1] & 0x40) !== 0 ? 'R' : 'N'
  const low = raw[0]
  const high = raw[1] & 0x3f
  if ((low >> 4) > 9 || (low & 0x0f) > 9 || (high >> 4) > 9 || (high & 0x0f) > 9) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  const value = high * 100 + (low >> 4) * 10 + (low & 0x0f)
  return digital
    ? { mode: 'dtcs', ctcss: null, dtcs: value, polarity }
    : { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const value = Math.round(channel.ctcss * 10)
    return Uint8Array.of(
      ((Math.floor(value / 10) % 10) << 4) | (value % 10),
      (Math.floor(value / 100) << 4) | (Math.floor(value / 1000) % 10),
    )
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const value = channel.dtcs
    return Uint8Array.of(
      ((Math.floor(value / 10) % 10) << 4) | (value % 10),
      0x80 |
        (channel.dtcsPolarity === 'R' ? 0x40 : 0) |
        (Math.floor(value / 100) & 0x3f),
    )
  }
  return Uint8Array.of(0xff, 0xff)
}

export function parseBaofengDigitalChannel(
  image: Uint8Array,
  profile: BaofengDigitalProfile,
  number: number,
): WebChannel {
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw.slice(0, 4).every((value) => value === 0 || value === 0xff)) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  const txFrequency = decodeLbcd(raw.slice(4, 8))
  if (!rxFrequency || !txFrequency) return emptyChannel(number)
  const txTone = decodeTone(raw.slice(8, 10))
  const rxTone = decodeTone(raw.slice(10, 12))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  const digital = (raw[12] & 0x02) !== 0
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency,
    toneMode: digital ? 'none' : selected.mode,
    ctcss: digital ? null : selected.ctcss,
    dtcs: digital ? null : selected.dtcs,
    dtcsPolarity: digital ? 'N' : selected.polarity,
    mode: raw[12] & 0x04 ? 'NFM' : 'FM',
    power: raw[12] & 0x08 ? 'High' : 'Low',
    skip: (raw[12] & 0x10) === 0,
  }
}

export function parseBaofengDigitalChannels(
  image: Uint8Array,
  profile: BaofengDigitalProfile,
): WebChannel[] {
  return Array.from({ length: profile.channelMaximum }, (_, index) =>
    parseBaofengDigitalChannel(image, profile, index + 1),
  )
}

export function applyBaofengDigitalChannel(
  image: Uint8Array,
  profile: BaofengDigitalProfile,
  channel: WebChannel,
): void {
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw.every((value) => value === 0xff)) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  encodeLbcd(channel.txFrequency ?? channel.rxFrequency, raw.subarray(4, 8))
  const digital = (raw[12] & 0x02) !== 0
  if (!digital) {
    raw.set(encodeTone(channel), 8)
    raw.set(encodeTone(channel), 10)
  }
  raw[12] =
    (raw[12] & 0xe1) |
    (channel.skip ? 0 : 0x10) |
    (channel.power === 'High' ? 0x08 : 0) |
    (channel.mode === 'NFM' ? 0x04 : 0) |
    (raw[12] & 0x03)
}

async function readExactAscii(
  transport: RadioTransport,
  text: string,
  length: number,
): Promise<Uint8Array> {
  await transport.write(new TextEncoder().encode(text))
  return transport.readExact(length, 3000)
}

async function enter(
  profile: BaofengDigitalProfile,
  transport: RadioTransport,
  write: boolean,
): Promise<void> {
  await transport.discardInput()
  if (profile.v12Unlock) {
    if (!(await readExactAscii(transport, '\u0002ucbfpwd', 1)).every((value) => value === ACK)) {
      throw new Error('The BF-V12D unlock command failed.')
    }
    await transport.write(Uint8Array.of(0x10, 0xc5, 0xea, 0x35))
    if ((await transport.readExact(1, 2500))[0] !== ACK) throw new Error('The BF-V12D unlock data was rejected.')
    await transport.write(new TextEncoder().encode('Q041OTUwMA==\u0000\u0000\u0000\u0000'))
    if ((await transport.readExact(1, 2500))[0] !== ACK) throw new Error('The BF-V12D model identifier was rejected.')
  }
  await transport.write(new TextEncoder().encode('\u0002prOGRAM'))
  const identity = await transport.readExact(4, 3000)
  if (identity[1] !== profile.serialId) {
    throw new Error(`The connected radio serial ID does not match ${profile.model}.`)
  }
  await transport.write(
    Uint8Array.of(0x02, profile.serialMagic[0], profile.serialMagic[1], ...new TextEncoder().encode('OGRAM')),
  )
  if ((await transport.readExact(1, 2500))[0] !== ACK) throw new Error('The radio rejected its protocol handshake.')
  const preamble: Array<[Uint8Array, Uint8Array]> = [
    [Uint8Array.of(0x02), Uint8Array.of(...new TextEncoder().encode('P3107'), 0xf7, 0x00, 0x00)],
    [Uint8Array.of(ACK), Uint8Array.of(ACK)],
    [
      Uint8Array.of(0x52, 0x01, 0x30, 0x08),
      Uint8Array.of(
        0x57,
        0x01,
        0x30,
        0x08,
        0xff,
        0xff,
        0xff,
        0xff,
        0xff,
        0xff,
        0xff,
        0xff,
      ),
    ],
    [Uint8Array.of(ACK), Uint8Array.of(ACK)],
  ]
  if (write) {
    preamble.push(
      [new TextEncoder().encode('E'), new TextEncoder().encode('F')],
      [Uint8Array.of(0x02, 0x80, 0x82, ...new TextEncoder().encode('OGRAM')), Uint8Array.of(ACK)],
      [Uint8Array.of(0x02), Uint8Array.of(...new TextEncoder().encode('P3107'), 0xf7, 0x00, 0x00)],
      [Uint8Array.of(ACK), Uint8Array.of(ACK)],
    )
  }
  for (const [command, expected] of preamble) {
    await transport.write(command)
    const response = await transport.readExact(expected.length, 3000)
    if (!response.every((value, index) => value === expected[index])) {
      throw new Error(`Unexpected preamble response: ${Array.from(response)}`)
    }
  }
}

async function exit(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

export async function readBaofengDigitalRadioWithTransport(
  profile: BaofengDigitalProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport, false)
  try {
    const image = new Uint8Array(profile.memorySize).fill(0xff)
    for (const [start, end] of memoryChunks(profile)) {
      for (let address = start; address < end; address += 8) {
        await transport.write(Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, 8))
        const response = await transport.readExact(12, 3000)
        image.set(response.slice(4), address)
        await transport.write(Uint8Array.of(ACK))
        if ((await transport.readExact(1, 2500))[0] !== ACK) {
          throw new Error(`The radio did not acknowledge read block 0x${address.toString(16)}.`)
        }
        onProgress?.({
          current: address,
          total: profile.memorySize,
          label: 'Reading Baofeng digital memory',
        })
      }
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exit(transport)
  }
}

export async function writeBaofengDigitalRadioWithTransport(
  profile: BaofengDigitalProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than Baofeng digital memory.')
  }
  await enter(profile, transport, true)
  try {
    for (const [start, end, readOnly] of memoryChunks(profile)) {
      if (readOnly) continue
      for (let address = start; address < end; address += 8) {
        const packet = new Uint8Array(12)
        packet.set([0x57, (address >> 8) & 0xff, address & 0xff, 8])
        packet.set(image.slice(address, address + 8), 4)
        await transport.write(packet)
        if ((await transport.readExact(1, 2500))[0] !== ACK) {
          throw new Error(`The radio rejected write block 0x${address.toString(16)}.`)
        }
        onProgress?.({
          current: address,
          total: profile.memorySize,
          label: 'Writing Baofeng digital memory',
        })
      }
    }
  } finally {
    await exit(transport)
  }
}
